/**
 * Lightweight hook for the unread notification count.
 *
 * Uses a plain useQuery (non-suspense) with its own dedicated cache key
 * so it can be consumed anywhere (Header, Sidebar, popout) without
 * suspending the parent tree.
 *
 * Mutations that change read status should invalidate
 * `notificationsKeys.unreadCount` so this value stays fresh.
 */

import { useQuery } from '@tanstack/react-query';
import { useEntity } from '../../../contexts/EntityContext';
import { fetchUnreadCount } from '../api';
import { notificationsKeys } from './useNotificationsQuery';
import { useAuth } from '../../../hooks/useAuth';
import { POLLING_INTERVAL_MS } from '../constants';
import { useFeatureFlag, FeatureFlagKey } from '../../../hooks/useFeatureFlags';

export function useUnreadCount(): number | undefined {
  const { activeEntity } = useEntity();
  const { getAccessTokenSilently } = useAuth();
  const entityId = activeEntity?.id ?? 'default';
  const notificationsEnabled = useFeatureFlag(FeatureFlagKey.PSP_UI_NOTIFICATIONS);

  const { data } = useQuery({
    queryKey: notificationsKeys.unreadCount(entityId),
    queryFn: async ({ signal }) => {
      const token = await getAccessTokenSilently();
      return fetchUnreadCount(token ?? '', entityId, undefined, signal);
    },
    enabled: notificationsEnabled && !!activeEntity?.id,
    // staleTime: 0 lets refetchOnWindowFocus (React Query default) fire immediately
    // when users return to the tab, so the bell shake animation can signal notifications
    // that arrived between polls.
    staleTime: 0,
    refetchInterval: POLLING_INTERVAL_MS,
  });

  return data?.count;
}
